import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { AllLabels, InstallationFlowType } from '../../../constants/app.constants';
import { FormTypesData, KeyValueData, TechnicianData } from '../../../domains/common.domain';
import { TechnicianDataService } from '../technician-data.service';
import { LookupOptionsData } from '../../jurisdiction.domain';
import { CommunicationService } from '../../../services/communication.service';
import { AllDropdownKeys, UrlSegments } from '../../jurisdiction.constants';
import { ErrorService } from '../../../services/error/error.service';
import * as _ from 'lodash';
import { EnvironmentService } from '../../../services/environment.service';
import { CommonService } from '../../../services/common.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { ValidationsService } from '../../../services/validations.service';
import { DestroyableComponent } from '../../../common/destroyable.component';
import { debounceTime, filter } from 'rxjs/operators';
import { UserService } from 'src/app/services/user.service';
import { SectionIdentifier } from 'src/app/+shared/components/review-section/review-section.domain';

@Component({
  selector: 'app-power-connection',
  styleUrls: ['./power-connection.component.scss'],
  templateUrl: './power-connection.component.html',
})
export class TechnicianPowerConnectionComponent extends DestroyableComponent implements OnChanges, OnInit {
  @Input() currentFormType: FormTypesData;
  @Input() powerConnectionData: any = null;
  @Input() isReadOnly: boolean = false;
  @Input() isDetailsView: boolean = false;
  @Input() applicationId: boolean = false;

  currentFlowType: string = this.localStorageService.getItem('installation_flow');
  infoBubblesData: {
    [key: string]: boolean;
  } = this.environmentService.getInfoBubblesData('technicianData') || null;
  powerConnectionFG: FormGroup;
  requiredFusePowerTypeKey: string = AllDropdownKeys.SWKNRequiredFusePower;
  requiredFusePowerTypes: LookupOptionsData[] = this.commonService.getDropdownData(AllDropdownKeys.SWKNRequiredFusePower);
  installationLocationTypes: LookupOptionsData[] = this.commonService.getDropdownData(AllDropdownKeys.PowerConnectionLocation);
  powerConnectionHeatingTypes: LookupOptionsData[] = this.commonService.getDropdownData(AllDropdownKeys.PowerConnectionHeatingType);
  showAddPowerDeviceButton: boolean = this.environmentService.addPowerDevice;
  showConnectedLoadLabel: boolean = this.environmentService.showConnectedLoadLabel;
  showStandardPowerWarning: boolean = this.environmentService.showStandardPowerWarning;
  showInfoForCommodities: boolean = this.environmentService.showInfoForCommodities;
  specialDeviceCount: number[] = [];
  specialDeviceData: any[];
  unitLabels: KeyValueData = AllLabels.units;
  usePowerConnectionExtendedOptionsForNewMains = this.environmentService.usePowerConnectionExtendedOptionsForNewMains;
  showSpecialConsumers = this.environmentService.showSpecialConsumers;
  technicianName = null;
  sectionIdentifier = SectionIdentifier;

  constructor(
    private commonService: CommonService,
    private communicationService: CommunicationService,
    private errorService: ErrorService,
    private technicianDataService: TechnicianDataService,
    public environmentService: EnvironmentService,
    private localStorageService: LocalStorageService,
    private cdr: ChangeDetectorRef,
    private userService: UserService
  ) {
    super();
  }

  ngOnInit(): void {
    this.prepareForm();
    this.handleChildForms();

    this.preventLeak(this.technicianDataService.registerForActionOnFormSubmit)
      .pipe(filter(value => !!value))
      .subscribe(() => {
        this.emitFormData();
        this.resetData();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes && changes.currentFormType && !changes.currentFormType.firstChange) ||
      (changes &&
        changes.powerConnectionData &&
        !changes.powerConnectionData.firstChange &&
        !_.isEqual(changes.powerConnectionData.previousValue, changes.powerConnectionData.currentValue))
    ) {
      this.specialDeviceCount = [];
      this.specialDeviceData = [];
      this.technicianDataService.manageSpecialConsumersFormData({
        component: 'specialDevice',
        formData: null,
        index: -1,
      });
      this.prepareForm();
    }
  }

  handleChildForms(): void {
    this.preventLeak(this.technicianDataService.registerForSpecialConsumersUpdate).subscribe(data =>
      (this.powerConnectionFG.controls.specialDevice as FormArray).push(data.formData)
    );
  }

  prepareData(): void {
    this.specialDeviceData = (this.powerConnectionData && this.powerConnectionData.specialDevice) || [];
    this.specialDeviceCount = [...Array(this.specialDeviceData.length).keys()];
  }

  addSpecialDevice(): void {
    this.specialDeviceCount.push(this.specialDeviceCount.length ? this.specialDeviceCount[this.specialDeviceCount.length - 1] + 1 : 0);
  }

  removeChildElements(index: number): void {
    let currentIndex = 0;
    const elementName = this.currentFormType.childElement;

    this[`${elementName}Count`].forEach((item, i) => {
      if (item === index) {
        currentIndex = i;
      }
    });

    this[`${elementName}Count`].splice(currentIndex, 1);
    (this.powerConnectionFG.controls[elementName] as FormArray).controls.splice(currentIndex, 1);
    (this.powerConnectionFG.controls[elementName] as FormArray).updateValueAndValidity();

    if (this.specialDeviceData.length && this.specialDeviceData[currentIndex]) {
      this.preventLeak(
        this.communicationService.deleteData(`${UrlSegments.UpdatePowerGridSpecialConsumer}${this.specialDeviceData[currentIndex].id}`)
      ).subscribe(
        response => {
          this.errorService.throwErrorIfFailure(response, true);
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    }
  }

  isFieldRequired(field: string) {
    const formField = this.powerConnectionFG.get(field);
    return ValidationsService.isFieldRequired(formField);
  }

  private prepareForm(): void {
    this.powerConnectionFG = new FormGroup({
      id: new FormControl((this.powerConnectionData && this.powerConnectionData.id) || ''),
      specialDevice: new FormArray([]),
    });

    if (
      this.environmentService.showTotalOutputField ||
      (this.currentFlowType !== InstallationFlowType.MainsConnection && this.currentFlowType !== InstallationFlowType.ChangeHouseConnection)
    ) {
      this.powerConnectionFG.addControl(
        'totalOutput',
        new FormControl(
          (this.powerConnectionData && this.powerConnectionData.totalOutput) || this.environmentService.defaultTotalPowerOutput,
          this.environmentService.totalOutputRequired ? [Validators.required] : null
        )
      );
    }

    if (
      this.environmentService.showFuseStrengthDropdown &&
      (this.currentFlowType === InstallationFlowType.MainsConnection || this.currentFlowType === InstallationFlowType.ChangeHouseConnection)
    ) {
      this.powerConnectionFG.addControl(
        'requiredFusePower',
        new FormControl((this.powerConnectionData && this.powerConnectionData.requiredFusePower) || '', [Validators.required])
      );
    }

    if (this.environmentService.powerConnectionShowEquipmentAvailableCheckboxes) {
      this.powerConnectionFG.addControl('craneAvailable', new FormControl(false, [Validators.required]));
      this.powerConnectionFG.addControl('pumpAvailable', new FormControl(false, [Validators.required]));
    }

    if (this.showAddPowerDeviceButton) {
      this.prepareData();
    }

    if (this.showPowerConnectionExtendendOptionsForNewMains) {
      this.powerConnectionFG.addControl(
        'installationLocationType',
        new FormControl(this.powerConnectionData?.installationLocationType, [Validators.required])
      );
      this.powerConnectionFG.addControl(
        'installationLocationTypeOther',
        new FormControl(this.powerConnectionData?.installationLocationTypeOther, [])
      );
      this.powerConnectionFG.addControl(
        'offerEarthworksOnPrivateProperty',
        new FormControl(this.powerConnectionData?.offerEarthworksOnPrivateProperty, [Validators.required])
      );
      this.powerConnectionFG.addControl(
        'powerConnectionHeatingType',
        new FormControl(this.powerConnectionData?.powerConnectionHeatingType, [Validators.required])
      );
      this.powerConnectionFG.addControl(
        'powerConnectionHeatingTypeOther',
        new FormControl(this.powerConnectionData?.powerConnectionHeatingTypeOther, [])
      );

      this.powerConnectionFG.controls.installationLocationType.valueChanges.subscribe(value => {
        if (value.toLowerCase() !== 'other') {
          this.powerConnectionFG.controls.installationLocationTypeOther.setValue('');
        }
      });

      this.powerConnectionFG.controls.powerConnectionHeatingType.valueChanges.subscribe(value => {
        if (value.toLowerCase() !== 'other') {
          this.powerConnectionFG.controls.powerConnectionHeatingTypeOther.setValue('');
        }
      });
    }

    if (this.showTempConnectionRegistrationForNewMains) {
      const bdewId = this.powerConnectionData?.technicianBdewId ?? this.technicianBdewId;
      this.powerConnectionFG.addControl(
        'requiredTempConnectionLess30',
        new FormControl({ value: this.powerConnectionData?.requiredTempConnectionLess30 ?? false, disabled: this.isReadOnly }, [])
      );
      this.powerConnectionFG.addControl('technicianBdewId', new FormControl(bdewId, []));
      this.powerConnectionFG.addControl('isTechnicianRegistered', new FormControl(!!bdewId, []));
      if (bdewId) {
        this.loadTechnicianByBdewId(bdewId);
      }

      this.powerConnectionFG.controls.requiredTempConnectionLess30.valueChanges.subscribe(value => {
        if (value) {
          this.powerConnectionFG.controls.technicianBdewId.setValidators([Validators.required]);
          this.powerConnectionFG.controls.technicianBdewId.setValue(this.technicianBdewId);
        } else {
          this.powerConnectionFG.controls.technicianBdewId.clearValidators();
          this.powerConnectionFG.controls.technicianBdewId.setValue('');
          this.powerConnectionFG.controls.isTechnicianRegistered.clearValidators();
          this.powerConnectionFG.controls.isTechnicianRegistered.updateValueAndValidity();
        }
        this.powerConnectionFG.controls.technicianBdewId.updateValueAndValidity();
      });

      this.preventLeak(this.powerConnectionFG.controls.technicianBdewId.valueChanges)
        .pipe(debounceTime(500))
        .subscribe(value => {
          this.powerConnectionFG.controls.isTechnicianRegistered.setValue(false);
          if (this.powerConnectionFG.controls.requiredTempConnectionLess30.value) {
            this.powerConnectionFG.controls.isTechnicianRegistered.setValidators([Validators.requiredTrue]);
            this.powerConnectionFG.controls.isTechnicianRegistered.updateValueAndValidity();
          }
          if (value) {
            this.loadTechnicianByBdewId(value);
          }
        });
    }

    if (this.showTempPowerReportConfirmationForNewMains) {
      this.powerConnectionFG.addControl(
        'tempPowerReportConfirmed',
        new FormControl({ value: !!this.powerConnectionData?.technicianBdewId, disabled: this.isReadOnly }, [Validators.requiredTrue])
      );
    }

    this.emitFormData();
    this.preventLeak(this.powerConnectionFG.valueChanges).subscribe(() => this.emitFormData());
    this.cdr.detectChanges();
  }

  get showPowerConnectionExtendendOptionsForNewMains(): boolean {
    return this.currentFlowType === InstallationFlowType.MainsConnection && this.usePowerConnectionExtendedOptionsForNewMains;
  }

  get showTempConnectionRegistrationForNewMains(): boolean {
    return (
      this.currentFlowType === InstallationFlowType.MainsConnection && this.environmentService.showTempConnectionRegistrationForNewMains
    );
  }

  get showTempPowerReportConfirmationForNewMains(): boolean {
    return this.showTempConnectionRegistrationForNewMains && this.isCustomer;
  }

  get showInfoTextRequiredFusePowerLarger200A(): boolean {
    return this.environmentService.showInfoTextRequiredFusePowerLarger200A;
  }

  get isCustomer(): boolean {
    return this.userService.isUserCustomerRole();
  }

  get isTechnician(): boolean {
    return this.userService.isUserTechnicianRole();
  }

  get technicianBdewId(): string {
    return this.isTechnician ? this.commonService?.technicianData?.bdew_id : '';
  }

  public setTechnicianFullName(technician: TechnicianData) {
    this.technicianName = technician ? `${technician.first_name} ${technician.last_name}` : null;
  }

  private emitFormData(): void {
    this.technicianDataService.registerForComponentFormUpdate.next({
      component: 'powerConnection',
      formData: this.powerConnectionFG,
    });
  }

  private resetData(): void {
    this.powerConnectionData = null;
  }

  private loadTechnicianByBdewId(bdewId: string): void {
    this.setTechnicianFullName(null);
    if (bdewId === this.technicianBdewId) {
      this.setTechnicianFullName(this.commonService?.technicianData);
      this.powerConnectionFG?.controls?.isTechnicianRegistered?.setValue(true);
    } else {
      this.preventLeak(this.communicationService.getData(UrlSegments.TechnicianByBdewIdAndPower.replace(':bdewid', bdewId))).subscribe(
        (response: TechnicianData) => {
          if (response?.technician_id) {
            this.setTechnicianFullName(response);
            this.powerConnectionFG?.controls?.isTechnicianRegistered?.setValue(true);
          }
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    }
  }
}
